import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../SleepHub/sleepHubApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  BigStatPanel,
  ProgressList,
  Chip,
  distributionBarOptions,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  AREA_FILL,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID,
  CHART_GRID_NO_X,
  toneChip,
  paletteAt
} from '../../component/common/designSystem';
import {
  CLICK_TYPE_COLORS,
  DISCOUNT_RAMP,
  EXPIRY_RAMP
} from './marketplaceTheme';

/**
 * Marketplace → VIP Offers Dashboard.
 *
 * Two halves that answer different questions, and the page is explicit about
 * which is which:
 *  - the CATALOGUE (from the offer library): what is on the shelf for your
 *    people — how many live offers, from how many brands, at what discount,
 *    and how much of it is about to lapse. This is Shoorah-wide content, so it
 *    reads the same for every company;
 *  - the ENGAGEMENT (from each offer's click history): what YOUR people did
 *    with it — clicks, what they clicked, and how often that carried them
 *    through to the partner.
 *
 * Engagement is derived from clickHistory rather than the offers' `clickCount`
 * column, which reads 0 even on offers with a dozen recorded clicks.
 */
const VipOffersDashboard = ({ fetcher, populationLabel = 'employees' }) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  const clicksPerDay = stats?.clicksPerDay || [];
  const clickTypeSplit = stats?.clickTypeSplit || [];
  const discountDistribution = stats?.discountDistribution || [];
  const expiryDistribution = stats?.expiryDistribution || [];
  const categorySplit = stats?.categorySplit || [];
  const topOffers = stats?.topOffers || [];

  // What share of the population ever clicked an offer.
  const reachPercentage =
    stats?.totalUsers > 0 ? Math.round((stats.clickers / stats.totalUsers) * 100) : null;

  // Clicks that stopped at the offer rather than carrying through to the
  // partner — the gap the through-put rate exists to show.
  const stalledClicks = Math.max((stats?.totalClicks || 0) - (stats?.throughPuts || 0), 0);

  const trendOptions = {
    chart: { ...CHART_BASE, type: 'area' },
    colors: [CHART_COLORS.brand],
    stroke: { curve: 'smooth', width: 2 },
    fill: AREA_FILL,
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: lastNDaysLabels(clicksPerDay.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} clicks` } }
  };

  const expiryOptions = distributionBarOptions({
    labels: expiryDistribution.map((band) => band.label),
    colors: EXPIRY_RAMP,
    unit: 'offers'
  });

  const categoryOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: categorySplit.map((entry, index) => paletteAt(index)),
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4, barHeight: '60%', distributed: true }
    },
    dataLabels: { enabled: true, style: { fontSize: '12px' } },
    legend: { show: false },
    grid: CHART_GRID_NO_X,
    xaxis: { categories: categorySplit.map((entry) => entry.label) },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} offers` } }
  };

  const offerOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: topOffers.map((offer, index) => paletteAt(index)),
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4, barHeight: '65%', distributed: true }
    },
    dataLabels: { enabled: true, style: { fontSize: '12px' } },
    legend: { show: false },
    grid: CHART_GRID_NO_X,
    xaxis: { categories: topOffers.map((offer) => offer.name) },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} clicks` } }
  };

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>VIP Offers Dashboard | Shoorah</title>
      </Helmet>
      {loader ? <Loader /> : null}

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.catalog}
          title="Live Offers"
          value={stats ? formatBigNumber(stats.liveOffers) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.audioLibrary}
          title="Partner Brands"
          value={stats ? formatBigNumber(stats.brands) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.rating}
          title="Avg Discount"
          value={stats ? `${stats.avgDiscount}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.plays}
          title="Offer Clicks"
          value={stats ? formatBigNumber(stats.totalClicks) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.users}
          title="People Browsing"
          value={stats ? formatBigNumber(stats.clickers) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Through to Partner"
          value={stats ? `${stats.throughPutRate}%` : '-'}
        />
      </div>

      {/* ---------- Catalogue ---------- */}
      <p className="mt-8 mb-3 text-sm font-medium text-shoorah-lightBlack dark:text-shoorah-darkBg">
        The catalogue
        <span className="ml-2 font-normal text-xs text-shoorah-gray3">
          What is on the shelf — the same library every company sees
        </span>
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <MiniDonutCard
          title="Discount Depth"
          subtitle="Advertised rate per offer"
          data={discountDistribution}
          colors={DISCOUNT_RAMP}
          unit="offers"
        />
        <MiniDonutCard
          title="Offer Runway"
          subtitle="Time left before expiry"
          data={expiryDistribution}
          colors={EXPIRY_RAMP}
          unit="offers"
        />
        <Panel title="Library Status" subtitle="Live, draft and lapsed offers">
          <ProgressList
            items={[
              { label: 'Live', count: stats?.liveOffers || 0 },
              { label: 'Draft', count: stats?.draftOffers || 0 },
              { label: 'Expired', count: stats?.expiredOffers || 0 },
              { label: 'Special coupon', count: stats?.specialCoupons || 0 }
            ]}
            unit="offers"
          />
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <Panel className="lg:col-span-2" title="Offers by Category" subtitle="What the library covers">
          {categorySplit.some((entry) => entry.count > 0) ? (
            <Chart
              type="bar"
              height={300}
              width="100%"
              series={[{ name: 'Offers', data: categorySplit.map((entry) => entry.count) }]}
              options={categoryOptions}
            />
          ) : (
            <PanelEmpty>No categorised offers yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Expiring Soon" subtitle="Offers by remaining runway">
          {expiryDistribution.some((band) => band.count > 0) ? (
            <Chart
              type="bar"
              height={300}
              width="100%"
              series={[{ name: 'Offers', data: expiryDistribution.map((band) => band.count) }]}
              options={expiryOptions}
            />
          ) : (
            <PanelEmpty>No offers carry an expiry date.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Engagement ---------- */}
      <p className="mt-8 mb-3 text-sm font-medium text-shoorah-lightBlack dark:text-shoorah-darkBg">
        Your people
        <span className="ml-2 font-normal text-xs text-shoorah-gray3">
          What your {populationLabel} actually did with it
        </span>
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <BigStatPanel
          title="Browsing Reach"
          subtitle={`Share of ${populationLabel} who opened an offer`}
          value={reachPercentage != null ? `${reachPercentage}%` : '-'}
          caption={
            stats
              ? `${formatBigNumber(stats.clickers)} of ${formatBigNumber(
                stats.totalUsers
              )} ${populationLabel}`
              : null
          }
        />

        <Panel className="lg:col-span-2" title="Offer Clicks" subtitle="Per day — last 30 days">
          {clicksPerDay.some((value) => value > 0) ? (
            <Chart
              type="area"
              height={300}
              width="100%"
              series={[{ name: 'Clicks', data: clicksPerDay }]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>No offer clicks in the last 30 days.</PanelEmpty>
          )}
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <MiniDonutCard
          title="What They Clicked"
          subtitle="Coupon code vs store link"
          data={clickTypeSplit}
          colors={CLICK_TYPE_COLORS}
          unit="clicks"
        />

        {/*
          Through-put is the only signal that a click became a visit to the
          partner, so the gap is worth showing as its own panel rather than
          buried in a percentage.
        */}
        <Panel title="Click Follow-through" subtitle="Did the click reach the partner">
          <ProgressList
            items={[
              { label: 'Carried through to partner', count: stats?.throughPuts || 0 },
              { label: 'Stopped at the offer', count: stalledClicks }
            ]}
            unit="clicks"
          />
        </Panel>

        <Panel title="Most Clicked Offers" subtitle={`Top ${topOffers.length || 10} by your people`}>
          {topOffers.length > 0 ? (
            <Chart
              type="bar"
              height={280}
              width="100%"
              series={[{ name: 'Clicks', data: topOffers.map((offer) => offer.clicks) }]}
              options={offerOptions}
            />
          ) : (
            <PanelEmpty>No offer clicks recorded yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {topOffers.length > 0 && (
        <Panel
          title="Top Offers Breakdown"
          subtitle="Clicks, discount and status per offer"
          className="mt-6"
        >
          <PanelTable
            head={
              <>
                <th className="py-2 pr-4 font-medium">#</th>
                <th className="py-2 pr-4 font-medium">Offer</th>
                <th className="py-2 pr-4 font-medium text-right">Clicks</th>
                <th className="py-2 pr-4 font-medium text-right">Discount</th>
                <th className="py-2 font-medium text-right">Status</th>
              </>
            }
          >
            {topOffers.map((offer, index) => (
              <tr key={offer.id || index} className={PANEL_ROW_CLASS}>
                <td className="py-2 pr-4">
                  {/* Rank swatch ties each row to its bar in the chart. */}
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: paletteAt(index) }}
                    />
                    {index + 1}
                  </span>
                </td>
                <td className="py-2 pr-4">{offer.name}</td>
                <td className="py-2 pr-4 text-right">{offer.clicks.toLocaleString()}</td>
                <td className="py-2 pr-4 text-right">
                  {offer.discount != null ? `${offer.discount}%` : '-'}
                </td>
                <td className="py-2 text-right">
                  <Chip className={toneChip(offer.isExpired ? 'danger' : 'success')}>
                    {offer.isExpired ? 'Expired' : 'Live'}
                  </Chip>
                </td>
              </tr>
            ))}
          </PanelTable>
        </Panel>
      )}
    </div>
  );
};

VipOffersDashboard.propTypes = {
  fetcher: PropTypes.func.isRequired,
  populationLabel: PropTypes.string
};

export default VipOffersDashboard;
